import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Radio } from "lucide-react";
import { RevealLine } from "./RevealLine";
import { PlayButton } from "./PlayButton";
import { EASE, KICK_URL } from "@/lib/site";

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 80, damping: 18 });
  const rotX = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 80, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const wordY = useTransform(scrollYProgress, [0, 1], [0, -120]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" ref={ref} onMouseMove={onMove} className="hero relative min-h-[100svh] overflow-hidden pt-28 sm:pt-32" data-testid="hero-section">
      <motion.div style={{ y: wordY }} className="hero-ghost pointer-events-none absolute inset-x-0 bottom-[6%] select-none text-center" aria-hidden>
        DAFABER
      </motion.div>

      <div className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 items-end gap-6 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-7 lg:pb-24">
          <motion.a
            href={KICK_URL} target="_blank" rel="noopener noreferrer"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.8, ease: EASE }}
            className="live-pill" data-testid="hero-live-pill"
          >
            <span className="live-dot" /> <Radio className="h-3.5 w-3.5" /> Live almost daily on Kick
          </motion.a>

          <h1 className="hero-title mt-6 font-display text-white" data-testid="hero-title">
            <RevealLine delay={0.5}>Spin loud.</RevealLine>
            <RevealLine delay={0.62}>Win <em className="hero-em">louder.</em></RevealLine>
            <RevealLine delay={0.74} className="text-[0.55em] font-bold tracking-tight text-white/90">Roobet with Dafaber.</RevealLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.9, ease: EASE }}
            className="mt-6 max-w-md text-base text-white/85 sm:text-lg" data-testid="hero-subtitle"
          >
            Casino streams almost every day, a $500 monthly leaderboard and giveaways for the community. Sign up with code <b className="text-[#FFC700]">DAFABER</b>.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.9, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <PlayButton size="lg" testId="hero-play-roobet-button" />
            <a href="#leaderboard" className="ghost-btn" data-testid="hero-leaderboard-link">View leaderboard</a>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5" style={{ perspective: 1200 }}>
          <motion.div style={{ y: imgY, rotateX: rotX, rotateY: rotY }} className="hero-cutout-wrap relative mx-auto aspect-square w-full max-w-[520px]">
            <div className="spotlight" aria-hidden />
            <motion.div
              initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
              animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
              transition={{ duration: 1.4, ease: EASE, delay: 0.35 }}
              className="relative h-full w-full"
            >
              <img src="/assets/dafaber-cutout.png" alt="Dafaber" className="h-full w-full object-contain object-bottom" data-testid="hero-cutout-image" />
            </motion.div>
            <motion.img
              src="/assets/roobet-icon.png" alt="Roobet"
              initial={{ scale: 0, rotate: -90 }} animate={{ scale: 1, rotate: 0 }} transition={{ delay: 1.3, duration: 1, ease: EASE }}
              className="chip-float absolute right-2 top-6 h-16 w-16 sm:h-20 sm:w-20"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
